/* eslint-disable jsx-a11y/anchor-is-valid */
import { css } from '@emotion/css'
import * as Switch from '@radix-ui/react-switch'
import _ from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useCustomReducer } from '../../../../../base/customHooks'
import { translate } from '../../../../../base/localization'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import IPButton from '../../../../../components/IPButton'
import IPModal from '../../../../../components/IPModal'
import IPSelectBox, { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import Table from '../../../../../components/Table'
import Translations from '../../../../../translations'
import { IAuthorizedServicesMain, IDescription } from '../models/models'
import { DeleteAuthorizedServicesMainApi } from '../services/deleteAuthorizedServicesMainApi'
import { GetAuthorizedServicesMainApi } from '../services/getAuthorizedServicesMainApi'
import { PutAuthorizedServicesMainApi } from '../services/putAuthorizedServicesApi'
import { getCompanyApi } from '../../../components/api'
import { ICompany } from '../../../components/models/models'

interface IState {
  showDescription: boolean

  companyListOptions: ISelectOption[]
  companyListOption: ISelectOption | null

  statusOptions: ISelectOption[]
  status: ISelectOption | null

  firmOptions: ISelectOption[]
  firmOption: ISelectOption | null
}

const LS_KEYS = {
  company: 'companyListOption',
  brand: 'brandSelectOption',
  status: 'statusOption',
  firm: 'firmOption',
} as const

function ListAuthorizedServices(props: BaseComponentWithContextProps) {
  const getInitialState = (): IState => ({
    showDescription: false,

    companyListOption: null,
    companyListOptions: [],

    statusOptions: [
      { label: 'Aktif', value: '1' },
      { label: 'Pasif', value: '0' },
    ],
    status: null,

    firmOptions: [],
    firmOption: null,
  })

  const initialState = getInitialState()
  const [state, setState] = useCustomReducer<IState>(initialState)

  const [authorizedServicesMain, setAuthorizedServicesMain] = useState<IAuthorizedServicesMain[]>([])
  const [filteredList, setFilteredList] = useState<IAuthorizedServicesMain[]>([])

  const [brandSelectOptions, setBrandSelectOptions] = useState<ISelectOption[]>([])
  const [brandSelectOption, setBrandSelectOption] = useState<ISelectOption | null>(null)

  const [des, setDes] = useState<IDescription>()
  const [brand, setBrand] = useState('')

  const navigate: any = useNavigate()
  const t = translate(Translations)

  // localStorage'dan güvenli okuma
  const safeParse = (val: string | null) => {
    if (!val) return null
    try {
      return JSON.parse(val)
    } catch {
      return null
    }
  }

  // İlk açılış: filtreleri restore et
  useEffect(() => {
    const storedCompany = safeParse(localStorage.getItem(LS_KEYS.company))
    const storedBrand = safeParse(localStorage.getItem(LS_KEYS.brand))
    const storedStatus = safeParse(localStorage.getItem(LS_KEYS.status))
    const storedFirm = safeParse(localStorage.getItem(LS_KEYS.firm))

    if (storedCompany) setState({ companyListOption: storedCompany })
    if (storedStatus) setState({ status: storedStatus })
    if (storedFirm) setState({ firmOption: storedFirm })
    if (storedBrand) setBrandSelectOption(storedBrand)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [pageIndex, setPageIndex] = useState<number>(0)

  useEffect(() => {
    const p = sessionStorage.getItem('Page')
    const n = p ? Number(p) : 0
    setPageIndex(!Number.isNaN(n) && n >= 0 ? n : 0)
  }, [])


  // Veri çek
  useEffect(() => {
    getCompanies()
    getAuthorizedServices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const getCompanies = async () => {
    try {
      const companyList: ICompany[] = await getCompanyApi()
      const companiesOptions: ISelectOption[] = companyList.map(company => ({
        label: company.Name,
        value: company.Id.toString(),
      }))
      setState({ companyListOptions: companiesOptions })
    } catch (err) {
      console.log('Fetch Companies Error:', err)
    }
  }

  const getAuthorizedServices = async () => {
    try {
      props.showLoading()

      const api: GetAuthorizedServicesMainApi = new GetAuthorizedServicesMainApi({})
      const result = await api.getAuthorizedServicesMain()

      if (result) {
        setAuthorizedServicesMain(result as any)
        setFilteredList(result)

        // Marka options (unique)
        const uniqueBrandLabels: Record<string, boolean> = {}
        const brandOpts = result
          .map(x => ({
            value: x.BrandId,
            label: x.Brand.Name.toUpperCase(),
          }))
          .filter(opt => {
            if (uniqueBrandLabels[opt.label]) return false
            uniqueBrandLabels[opt.label] = true
            return true
          })
          .sort((a, b) => a.label.localeCompare(b.label, 'tr'))
        setBrandSelectOptions(brandOpts)

        // Firma options (Description.CompanyName unique)
        const uniqueFirmLabels: Record<string, boolean> = {}
        const firmOpts = result
          .map(x => ({
            value: (x.Description?.CompanyName || '').trim(),
            label: (x.Description?.CompanyName || '').trim(),
          }))
          .filter(x => x.value)
          .filter(opt => {
            if (uniqueFirmLabels[opt.label]) return false
            uniqueFirmLabels[opt.label] = true
            return true
          })
          .sort((a, b) => a.label.localeCompare(b.label, 'tr'))

        setState({ firmOptions: firmOpts })
      }
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Yetkili Servisler getirilirken bir hata oluştu.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } finally {
      props.hideLoading()
    }
  }

  // Filtre uygulama (BUTON + OTOMATİK)
  const applyFilters = useCallback(() => {
    const conditions: Array<(item: IAuthorizedServicesMain) => boolean> = []

    // Marka
    if (brandSelectOption) {
      conditions.push(x => x.Brand.Name.toUpperCase() === brandSelectOption.label)
    }

    // Şirket (brand.Companies)
    if (state.companyListOption) {
      const companyVal = state.companyListOption.value
      conditions.push(x => x.Brand.Companies.includes(companyVal))
    }

    // Durum (Aktif/Pasif)
    if (state.status) {
      const desired = state.status.value === '1'
      conditions.push(x => x.Enable === desired)
    }

    // Firma (Description.CompanyName)
    if (state.firmOption) {
      const firm = (state.firmOption.value || '')
      conditions.push(x => (x.Description?.CompanyName || '').trim() === firm)
    }

    let data = authorizedServicesMain
    if (conditions.length > 0) {
      data = data.filter(item => conditions.every(cond => cond(item)))
    }
    setFilteredList(data)
  }, [authorizedServicesMain, brandSelectOption, state.companyListOption, state.status, state.firmOption])

  // ✅ Otomatik filtreleme:
  // - liste yüklendiğinde
  // - localStorage'dan restore edilen filtreler set edildiğinde
  // - filtre seçimi değiştiğinde
  useEffect(() => {
    if (!authorizedServicesMain || authorizedServicesMain.length === 0) return
    applyFilters()
  }, [applyFilters, authorizedServicesMain])

  // ✅ Filtre değiştikçe localStorage yaz (butona basmaya gerek yok)
  useEffect(() => {
    if (state.companyListOption) localStorage.setItem(LS_KEYS.company, JSON.stringify(state.companyListOption))
    else localStorage.removeItem(LS_KEYS.company)

    if (brandSelectOption) localStorage.setItem(LS_KEYS.brand, JSON.stringify(brandSelectOption))
    else localStorage.removeItem(LS_KEYS.brand)

    if (state.status) localStorage.setItem(LS_KEYS.status, JSON.stringify(state.status))
    else localStorage.removeItem(LS_KEYS.status)

    if (state.firmOption) localStorage.setItem(LS_KEYS.firm, JSON.stringify(state.firmOption))
    else localStorage.removeItem(LS_KEYS.firm)
  }, [state.companyListOption, brandSelectOption, state.status, state.firmOption])

  const update = async (Id: any) => {
    const data = authorizedServicesMain.find(x => x.Id === Id)
    if (!data) return

    const clnValues = _.clone(data)
    clnValues.Enable = !clnValues.Enable

    if (Array.isArray(clnValues.BrandIds) && clnValues.BrandIds.length > 0) {
      clnValues.BrandIds[0] = clnValues.BrandId
    } else {
      clnValues.BrandIds = [clnValues.BrandId]
    }

    const putApi: PutAuthorizedServicesMainApi = new PutAuthorizedServicesMainApi({})
    putApi
      .putAuthorizedServicesMain(clnValues)
      .then(async result => {
        if (result) {
          Swal.fire({
            icon: 'success',
            title: 'Yetkili Servis Güncellendi',
            showConfirmButton: true,
            allowOutsideClick: false,
          }).then(async res => {
            if (res) {
              setAuthorizedServicesMain(prev => prev.map(row => (row.Id === Id ? { ...row, Enable: clnValues.Enable } : row)))
              setFilteredList(prev => prev.map(row => (row.Id === Id ? { ...row, Enable: clnValues.Enable } : row)))
            }
          })
        }
      })
      .catch((err: any) => {
        Swal.fire({
          icon: 'error',
          title: err.description,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      })
  }

  const deleteAuthorizedServicesMain = async (Id: any) => {
    if (!Id) return

    Swal.fire({
      icon: 'question',
      title: 'Yetkili servis silmek istediğinize emin misiniz?',
      showConfirmButton: true,
      allowOutsideClick: false,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
    }).then(async result => {
      if (result.isConfirmed) {
        const deleteApi: DeleteAuthorizedServicesMainApi = new DeleteAuthorizedServicesMainApi({})
        deleteApi
          .deleteAuthorizedServicesMain(Id)
          .then(async res => {
            if (res) {
              Swal.fire({
                icon: 'success',
                title: 'Yetkili servis Silindi',
                showConfirmButton: true,
                allowOutsideClick: false,
              }).then(async ok => {
                if (ok) {
                  setAuthorizedServicesMain(prev => prev.filter(row => row.Id !== Id))
                  setFilteredList(prev => prev.filter(row => row.Id !== Id))
                }
              })
            }
          })
          .catch((err: any) => {
            Swal.fire({
              icon: 'error',
              title: err.description,
              showConfirmButton: true,
              allowOutsideClick: false,
            })
          })
      }
    })
  }

  const clearFilters = () => {
    setState({
      companyListOption: null,
      status: null,
      firmOption: null,
    })
    setBrandSelectOption(null)

    localStorage.removeItem(LS_KEYS.company)
    localStorage.removeItem(LS_KEYS.brand)
    localStorage.removeItem(LS_KEYS.status)
    localStorage.removeItem(LS_KEYS.firm)

    setFilteredList(authorizedServicesMain)
  }

  //#region Tablo
  const tableColumns = useMemo(
    () => [
      { Header: 'Aktif', accessor: 'Enable' },
      { Header: 'Marka', accessor: 'BrandName' },
      { Header: 'Sıra', accessor: 'OrderNo' },
      { Header: 'Başlangıç Tarihi', accessor: 'StartDate' },
      { Header: 'Bitiş Tarihi', accessor: 'EndDate' },
      { Header: 'Açıklama', accessor: 'Description' },
      { Header: 'Firma', accessor: 'CompanyName' },
      { Header: 'İşlemler', accessor: 'Actions', disableSortBy: true },
    ],
    []
  )

  const tableData = useMemo(() => {
    return filteredList.map(dt => ({
      Id: dt.Id,
      Enable: (
        <Switch.Root className='SwitchRoot' id='active' checked={dt.Enable} onCheckedChange={() => update(dt.Id)}>
          <Switch.Thumb className='SwitchThumb' />
        </Switch.Root>
      ),
      BrandName: dt.Brand.Name,
      OrderNo: dt.OrderNo,
      StartDate: new Date(dt.StartDate).toLocaleDateString('tr-TR'),
      EndDate: new Date(dt.EndDate).toLocaleDateString('tr-TR'),
      Description: (
        <IPButton
          text='Detay'
          onClick={() => {
            setState({ showDescription: true })
            setDes(dt.Description)
            setBrand(dt.Brand.Name)
          }}
          type='button'
          style={{ padding: '0px', width: '120px', height: '30px', color: 'white', borderColor: 'red' }}
          className='btn btn-danger'
        />
      ),
      CompanyName: (
        <div title={dt.Description.CompanyName}>
          {dt.Description.CompanyName.slice(0, 40) + (dt.Description.CompanyName.length > 40 ? ' ...' : '')}
        </div>
      ),
      Actions: (
        <>
          <a
            data-action-type='edit'
            role='button'
            style={{ cursor: 'pointer' }}
            data-toggle='tooltip'
            data-original-title={t('pages.mySpecial.userUpdate.edit')}
            onClick={() => {
              navigate('/admin/brandPageOperation/AuthorizedServices/AddAuthorizedServices', {
                state: { authorizedServicesProcedurData: dt },
              })
            }}
          >
            <i
              className={`${css`
                font-size: 1.2rem;
                color: green;
              `} icon-edit`}
            ></i>
          </a>
          <a
            onClick={() => deleteAuthorizedServicesMain(dt.Id)}
            data-action-type='delete'
            role='button'
            className={css`
              cursor: pointer;
            `}
            data-toggle='tooltip'
            data-original-title={t('pages.mySpecial.userUpdate.delete')}
          >
            <i
              className={`${css`
                font-size: 1.2rem;
                color: red;
              `} icon-trash-2`}
            ></i>
          </a>
        </>
      ),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredList])
  //#endregion

  return (
    <React.Fragment>
      <div className=''>
        <div className='content col-lg-12 m-auto'>
          <div className='card'>
            <div className='card-header d-flex align-items-center'>
              <span className='h4 mb-0'>Yetkili Servisler</span>

              <div className='d-flex gap-2 ms-auto me-3'>
                <Link to='/admin/brandPageOperation/AuthorizedServices/AddAuthorizedServicesVodafone'>
                  <button type='button' className='btn btn-primary'>
                    Vodafone İçin Ekle
                  </button>
                </Link>

                <Link to='/admin/brandPageOperation/AuthorizedServices/AddAuthorizedServices'>
                  <button type='button' className='btn btn-primary'>
                    Ekle
                  </button>
                </Link>
              </div>
            </div>

            <div className='card-body'>
              <div className='detailFilter'>
                <div className='row ms-1'>
                  <div className='form-group col-md-3'>
                    <IPSelectBox
                      isClearable
                      placeholder='Şirket'
                      options={state.companyListOptions}
                      value={state.companyListOption}
                      onChangeSingle={option => setState({ companyListOption: option })}
                    />
                  </div>

                  <div className='form-group col-md-3'>
                    <IPSelectBox
                      isClearable
                      placeholder='Marka'
                      options={brandSelectOptions}
                      value={brandSelectOption}
                      onChangeSingle={option => setBrandSelectOption(option)}
                    />
                  </div>

                  <div className='form-group col-md-2'>
                    <IPSelectBox
                      isClearable
                      placeholder='Durum'
                      options={state.statusOptions}
                      value={state.status}
                      onChangeSingle={option => setState({ status: option })}
                    />
                  </div>

                  <div className='form-group col-md-3'>
                    <IPSelectBox
                      isClearable
                      placeholder='Firma'
                      options={state.firmOptions}
                      value={state.firmOption}
                      onChangeSingle={option => setState({ firmOption: option })}
                    />
                  </div>

                  <div className='form-group col-md-1 d-flex gap-2'>
                    <IPButton
                      type='button'
                      className='btn btn-primary'
                      text='Filtrele'
                      onClick={applyFilters}
                      style={{ height: '45px', width: '100%' }}
                    />
                  </div>
                </div>

                <div className='row ms-1 mt-2'>
                  <div className='form-group col-md-2'>
                    <IPButton
                      type='button'
                      className='btn btn-secondary'
                      text='Temizle'
                      onClick={clearFilters}
                      style={{ height: '40px', width: '100%' }}
                    />
                  </div>
                </div>
              </div>

              <div className='m-4 text-nowrap'>
                <Table columns={tableColumns} data={tableData} textAlign={true} color='white' currentPage={pageIndex} />
              </div>
            </div>
          </div>

          <IPModal
            show={state.showDescription}
            onClose={() => setState({ showDescription: false })}
            hideConfirm={true}
            width={1000}
            title={brand + ' YETKİLİ SERVİS NOKTASI'}
          >
            <div className='col'>
              <div className='d-flex align-items-center justify-content-start'>
                <b style={{ width: '400px' }}>Ürün Kodu</b> <span className='mr-2'>:</span>
                {des?.ProductGroup}
              </div>
              <div className='d-flex align-items-center justify-content-start'>
                <b style={{ width: '400px' }}>Firma İsmi</b>
                <span className='mr-2'>:</span>
                {des?.CompanyName}
              </div>
              <div className='d-flex align-items-center justify-content-start'>
                <b style={{ width: '400px' }}>Adres</b> <span className='mr-2'>:</span> {des?.Address}
              </div>
              <div className='d-flex align-items-center justify-content-start'>
                <b style={{ width: '400px' }}>Telefon</b> <span className='mr-2'>:</span>
                {des?.Phone}
              </div>
              <div className='d-flex align-items-center justify-content-start'>
                <b style={{ width: '400px' }}>E-Posta</b>
                <span className='mr-2'>:</span>
                {des?.Email}
              </div>
              <div className='d-flex align-items-center justify-content-start'>
                <b style={{ width: '400px' }}>Web Adresi</b> <span className='mr-2'>:</span>{' '}
                <a href={des?.WebAddress}>{des?.WebAddress}</a>
              </div>
              <div className='d-flex align-items-center justify-content-start'>
                <b style={{ width: '400px' }}>Ücret ödemeli kargo kabul ediliyor mu?</b> <span className='mr-2'>:</span>{' '}
                {des?.IsChargedShippingAccepted}
              </div>
              <div className='d-flex align-items-center justify-content-start'>
                <b style={{ width: '400px' }}>Anlaşmalı kargo şirketi(varsa gönderi kodu)</b>{' '}
                <span className='mr-2'>:</span> {des?.ContractedCargoCompany}
              </div>
              <div className='d-flex align-items-center justify-content-start'>
                <b style={{ width: '400px' }}>Yetkili servis noktaları</b> <span className='mr-2'>:</span>{' '}
                {des?.AuthorizedServicePoints}
              </div>
            </div>
          </IPModal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default componentWithContext(ListAuthorizedServices)
